// components/masterdata/AblageorteListe.jsx

import React, {useState, useEffect, useCallback} from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router';
import Navbar from '../utils/Navbar';

const AblageorteListe = () => {
  const { fetchWithAuth, accessToken } = useAuth();
  const navigate = useNavigate();

  const [ablageorte, setAblageorte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [newAblageort, setNewAblageort] = useState({
    name: "",
    order: ""
  });

  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});

  const fetchAblageorte = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`/api/data/ablageorte`, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Ablageorte');
      }

      const data = await response.json();
      setAblageorte(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  const handleSave = async () => {
    try {
      for (const id in editData) {
        await fetchWithAuth(`/api/data/ablageorte/${id}/`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editData[id]),
        });
      }
      setEditMode(false);
      fetchAblageorte();
    } catch (err) {
      alert("Fehler beim Speichern: " + err.message);
    }
  };

  const handleDeleteSingle = async (id) => {
    if (!window.confirm("Willst du diesen Ablageort wirklich löschen?")) return;

    try {
      await fetchWithAuth(`/api/data/ablageorte/${id}/`, {
        method: "DELETE",
      });
      fetchAblageorte(); // Liste neu laden
    } catch (err) {
      alert("Fehler beim Löschen: " + err.message);
    }
  };

  const handleAddAblageorte = async () => {
    if (!newAblageort.name.trim()) return;

    try {
      const response = await fetchWithAuth(`/api/data/ablageorte/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newAblageort)
      });

      if (response.ok) {
        setNewAblageort({ name: "", order: "" });
        fetchAblageorte();
      } else {
        alert('Fehler beim Hinzufügen');
      }
    } catch (err) {
      alert('Netzwerkfehler: ' + err.message);
    }
  };

  useEffect(() => {
    if (!accessToken) return;
    fetchAblageorte();
  }, [accessToken, fetchAblageorte]);

  if (loading) return <div>Lade Ablageorte...</div>;
  if (error) return <div className="text-danger">Fehler: {error}</div>;

  return (
    <>
      <Navbar />
      <div className="container mt-4">
        <h2 className="mb-4">Ablageorte verwalten</h2>

        <div className="d-flex mb-4 gap-2">
          <input
            className="form-control"
            type="text"
            placeholder="Neuer Ablageort..."
            value={newAblageort.name}
            onChange={(e) => setNewAblageort({ ...newAblageort, name: e.target.value })}
          />
          <input
            className="form-control"
            type="text"
            placeholder="Sortierung"
            value={newAblageort.order}
            onChange={(e) => setNewAblageort({ ...newAblageort, order: e.target.value })}
          />
          <button className="btn btn-success" onClick={handleAddAblageorte} disabled={editMode}>
            Hinzufügen
          </button>
        </div>

        {editMode && (
          <div className="alert alert-warning d-flex align-items-center" role="alert">
            <i className="bi bi-pencil-square me-2"></i>
            Bearbeitungsmodus aktiv – Änderungen noch nicht gespeichert!
          </div>
        )}

        <table className="table table-striped">
          <thead>
            <tr>
              {editMode && <th>Aktion</th>}
              <th>ID</th>
              <th>Name</th>
              <th>Sortierung</th>
            </tr>
          </thead>
          <tbody>
            {ablageorte.map((kat) => (
              <tr key={kat.id}>
                {editMode && (
                  <td>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteSingle(kat.id)}
                    >
                      🗑️
                    </button>
                  </td>
                )}
                <td>{kat.id}</td>

                {/* Name */}
                <td>
                  {editMode ? (
                    <input
                      type="text"
                      className="form-control"
                      value={editData[kat.id]?.name || ""}
                      onChange={(e) =>
                        setEditData({...editData, [kat.id]: { ...editData[kat.id], name: e.target.value }
                        })
                      }
                    />
                  ) : (
                    kat.name
                  )}
                </td>

                {/* Order */}
                <td>
                  {editMode ? (
                    <input
                      type="number"
                      className="form-control"
                      value={editData[kat.id]?.order || ""}
                      onChange={(e) =>
                        setEditData({...editData, [kat.id]: { ...editData[kat.id], order: e.target.value }
                        })
                      }
                    />
                  ) : (
                    kat.order
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="d-flex flex-wrap gap-2 mb-4">
          {editMode ? (
            <button
              className="btn btn-warning"
              onClick={() => {
                setEditMode(false);
                setEditData({});
              }}
            >
              Abbrechen
            </button>
          ) : (
            <button
              className="btn btn-secondary"
              onClick={() => navigate("/objekte")}
            >
              Zurück
            </button>
          )}

          {!editMode && (
            <button className="btn btn-primary" onClick={() => {
              setEditMode(true);
              const initialData = {};
              ablageorte.forEach(k => {
                initialData[k.id] = {
                  name: k.name,
                  order: k.order
                };
              });
              setEditData(initialData);
            }}>
              Bearbeiten
            </button>
          )}

          {editMode && (
              <button className="btn btn-success" onClick={handleSave}>
                Änderungen speichern
              </button>
          )}
        </div>
      </div>
    </>
  );
};

export default AblageorteListe;
